import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PauseCircleRoundedIcon from '@mui/icons-material/PauseCircleRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import axiosInstance from '../../../utils/axiosInstance';
import { OUTPUT_KINDS } from '../../../utils/capabilityContract';
import AssetRunCard from '../../AssetRunCard';


const taskId = (task = {}) => task.workstream_task_id || task.task_id || '';

const userLabel = (user = {}) => (
  user.full_name || user.email || user.cognito_sub || user.user_id || 'Unassigned'
);

const formatLabel = (value) => String(value || 'unknown')
  .split(/[_\s-]+/)
  .filter(Boolean)
  .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
  .join(' ');

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const statusColor = (status) => {
  if (status === 'completed') return 'success';
  if (status === 'waiting') return 'warning';
  if (status === 'cancelled') return 'default';
  if (status === 'in_progress') return 'info';
  return 'default';
};

const eligibleUsers = (users) => (Array.isArray(users) ? users : [])
  .filter((user) => user.has_access && String(user.status || '').toLowerCase() === 'active');

async function fetchBusinessUnitUsers(workstream) {
  const businessUnitId = workstream?.provider_business_unit?.business_unit_id;
  if (!businessUnitId) return [];
  const response = await axiosInstance.get(
    `/partners/user/business-units/${encodeURIComponent(businessUnitId)}/users`,
  );
  return eligibleUsers(response.data?.users);
}

function CreateTaskDialog({ open, workstream, onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    assignee: null,
    dueAt: '',
    outputName: '',
    outputKind: 'file',
    outputDescription: '',
    capabilityOutputKey: '',
  });

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingUsers(true);
    setError('');
    fetchBusinessUnitUsers(workstream)
      .then((items) => {
        if (!cancelled) setUsers(items);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.message || err?.message || 'Unable to load Business Unit users.');
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => { cancelled = true; };
  }, [open, workstream]);

  const compatibleCapabilityOutputs = useMemo(() => (
    (workstream?.expected_outputs || []).filter((output) => (
      String(output.kind || 'file').toLowerCase() === form.outputKind
    ))
  ), [form.outputKind, workstream]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const close = (force = false) => {
    if (submitting && !force) return;
    setForm({
      title: '',
      description: '',
      assignee: null,
      dueAt: '',
      outputName: '',
      outputKind: 'file',
      outputDescription: '',
      capabilityOutputKey: '',
    });
    setError('');
    onClose();
  };

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.assignee || !form.outputName.trim()) {
      setError('Task name, description, assignee, and output name are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(workstream.request_capability_id)}/tasks`,
        {
          title: form.title.trim(),
          description: form.description.trim(),
          assignee_user_id: form.assignee.cognito_sub,
          due_at: form.dueAt ? new Date(form.dueAt).toISOString() : null,
          expected_output: {
            name: form.outputName.trim(),
            description: form.outputDescription.trim() || null,
            kind: form.outputKind,
          },
          capability_output_key: form.capabilityOutputKey || null,
        },
      );
      close(true);
      await onCreated();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to create Task.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Create Task</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Typography variant="overline" color="text.secondary">Task Details</Typography>
          <TextField
            label="Task Name"
            required
            value={form.title}
            onChange={(event) => setField('title', event.target.value)}
            autoFocus
          />
          <TextField
            label="Description"
            required
            multiline
            minRows={3}
            value={form.description}
            onChange={(event) => setField('description', event.target.value)}
          />
          <Autocomplete
            options={users}
            value={form.assignee}
            loading={loadingUsers}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(option, value) => option.cognito_sub === value.cognito_sub}
            onChange={(_event, value) => setField('assignee', value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign To"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            label="Due Date"
            type="datetime-local"
            value={form.dueAt}
            onChange={(event) => setField('dueAt', event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Divider />
          <Typography variant="overline" color="text.secondary">Expected Output</Typography>
          <TextField
            label="Output Name"
            required
            value={form.outputName}
            onChange={(event) => setField('outputName', event.target.value)}
          />
          <TextField
            label="Output Type"
            select
            required
            value={form.outputKind}
            onChange={(event) => setForm((current) => ({
              ...current,
              outputKind: event.target.value,
              capabilityOutputKey: '',
            }))}
          >
            {OUTPUT_KINDS.map((kind) => (
              <MenuItem key={kind.value} value={kind.value}>{kind.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Output Description"
            multiline
            minRows={2}
            value={form.outputDescription}
            onChange={(event) => setField('outputDescription', event.target.value)}
          />
          <TextField
            label="Contributes to Capability Output"
            select
            value={form.capabilityOutputKey}
            onChange={(event) => setField('capabilityOutputKey', event.target.value)}
            helperText="Only compatible Capability outputs are shown. Linked results are shared through the fulfillment contract."
          >
            <MenuItem value="">None</MenuItem>
            {compatibleCapabilityOutputs.map((output) => (
              <MenuItem key={output.key || output.output_key} value={output.key || output.output_key}>
                {output.name || formatLabel(output.key || output.output_key)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={submitting || loadingUsers}>
          {submitting ? 'Creating...' : 'Create Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TaskOutputProvider({ task, workstream, onProvided, onError }) {
  const outputDefinition = task?.expected_output_definition || task?.expected_output || {};
  const kind = String(outputDefinition.kind || 'file').toLowerCase();
  const [file, setFile] = useState(null);
  const [value, setValue] = useState(kind === 'value' ? '' : '{}');
  const [description, setDescription] = useState('');
  const [runIds, setRunIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setFile(null);
    setValue(kind === 'value' ? '' : '{}');
    setDescription('');
    setRunIds([]);
  }, [kind, task?.workstream_task_id]);

  const submit = async () => {
    setSubmitting(true);
    onError('');
    try {
      let payload = { description: description.trim() || undefined };
      if (kind === 'file') {
        if (!file) throw new Error('Choose a file before providing the output.');
        payload = {
          ...payload,
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          size_bytes: file.size,
        };
      } else if (kind === 'structured_data') {
        payload.structured_value = JSON.parse(value);
      } else if (kind === 'physical_item') {
        payload.physical_item_metadata = JSON.parse(value);
      } else if (kind === 'asset_run') {
        if (!runIds.length) throw new Error('Select at least one Run before providing the output.');
        payload.run_ids = runIds;
      } else {
        payload.value = value;
      }

      const response = await axiosInstance.post(
        `/workstream-tasks/${encodeURIComponent(taskId(task))}/output`,
        payload,
      );
      const uploadUrl = response.data?.upload_url || response.data?.url;
      if (uploadUrl && file) {
        const upload = await fetch(uploadUrl, {
          method: response.data?.method || 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!upload.ok) throw new Error(`Upload failed with status ${upload.status}.`);
      }
      await onProvided();
    } catch (err) {
      onError(err?.response?.data?.message || err?.message || 'Unable to provide Task output.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      {kind === 'file' ? (
        <Button variant="outlined" component="label" startIcon={<UploadFileRoundedIcon />}>
          {file ? file.name : 'Choose File'}
          <input hidden type="file" onChange={(event) => setFile(event.target.files?.[0] || null)} />
        </Button>
      ) : kind === 'asset_run' ? (
        <Stack spacing={0.75}>
          <Typography variant="body2" color="text.secondary">Select associated Runs</Typography>
          {(workstream?.runs || []).length ? workstream.runs.map((run) => (
            <FormControlLabel
              key={run.run_id}
              control={<Checkbox checked={runIds.includes(run.run_id)} onChange={() => setRunIds((current) => current.includes(run.run_id) ? current.filter((id) => id !== run.run_id) : [...current, run.run_id])} />}
              label={`${run.asset?.asset_name || 'Asset'} · ${String(run.run_id).slice(0, 8)} · ${formatLabel(run.status)}`}
            />
          )) : <Alert severity="info">No associated Runs are available.</Alert>}
        </Stack>
      ) : (
        <TextField
          label={kind === 'value' ? 'Value' : kind === 'physical_item' ? 'Physical Item Metadata (JSON)' : 'Structured Data (JSON)'}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          multiline={kind !== 'value'}
          minRows={kind === 'value' ? undefined : 5}
          required
        />
      )}
      <TextField
        label="Description"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        multiline
        minRows={2}
      />
      <Box>
        <Button variant="contained" onClick={submit} disabled={submitting || (kind === 'asset_run' && !runIds.length)}>
          {submitting ? 'Providing...' : 'Provide Output'}
        </Button>
      </Box>
    </Stack>
  );
}

function TaskOutputSummary({ task, onError }) {
  const output = task?.output;
  const download = async () => {
    onError('');
    try {
      const response = await axiosInstance.post(
        `/workstream-tasks/${encodeURIComponent(taskId(task))}/output/download`,
      );
      const link = document.createElement('a');
      link.href = response.data?.download_url || response.data?.url;
      link.target = '_blank';
      link.download = response.data?.filename || output?.filename || '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      onError(err?.response?.data?.message || err?.message || 'Unable to download Task output.');
    }
  };

  if (!output) {
    return <Typography variant="body2" color="text.secondary">No output provided.</Typography>;
  }
  if ((output.runs || []).length) {
    return (
      <Stack spacing={1}>
        {output.runs.map((run) => <AssetRunCard key={run.run_id} run={run} compact />)}
        {output.shared_via_capability ? <Chip size="small" color="success" label="Shared via Capability Output" /> : <Chip size="small" label="Provider Internal" />}
      </Stack>
    );
  }
  const displayed = output.filename
    || output.value
    || JSON.stringify(output.structured_value || output.physical_item_metadata || {}, null, 2);
  return (
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {displayed}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {output.shared_via_capability ? <Chip size="small" color="success" label="Shared via Capability Output" /> : <Chip size="small" label="Provider Internal" />}
        {output.artifact_id ? (
          <Button size="small" startIcon={<DownloadRoundedIcon />} onClick={download}>Download</Button>
        ) : null}
      </Stack>
    </Stack>
  );
}

function WorkstreamTaskDetail({ open, initialTask, workstream, onClose, onChanged }) {
  const [task, setTask] = useState(initialTask);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [actionMode, setActionMode] = useState('');
  const [reason, setReason] = useState('');
  const [users, setUsers] = useState([]);
  const [newAssignee, setNewAssignee] = useState(null);

  const load = async () => {
    const id = taskId(initialTask || task);
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(`/workstream-tasks/${encodeURIComponent(id)}`);
      setTask(response.data?.task || response.data);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to load Task.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open, initialTask?.workstream_task_id, initialTask?.task_id]);

  useEffect(() => {
    if (!open || !task?.authorization?.can_reassign) return undefined;
    let cancelled = false;
    fetchBusinessUnitUsers(workstream).then((items) => {
      if (!cancelled) setUsers(items);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, task?.authorization?.can_reassign, workstream]);

  const changed = async () => {
    await load();
    await onChanged();
  };

  const postAction = async (action, body = {}) => {
    setBusy(true);
    setError('');
    try {
      await axiosInstance.post(`/workstream-tasks/${encodeURIComponent(taskId(task))}/${action}`, body);
      setActionMode('');
      setReason('');
      await changed();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Unable to ${action} Task.`);
    } finally {
      setBusy(false);
    }
  };

  const reassign = async () => {
    if (!newAssignee) return;
    setBusy(true);
    setError('');
    try {
      await axiosInstance.patch(`/workstream-tasks/${encodeURIComponent(taskId(task))}`, {
        assignee_user_id: newAssignee.cognito_sub,
      });
      setActionMode('');
      setNewAssignee(null);
      await changed();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to reassign Task.');
    } finally {
      setBusy(false);
    }
  };

  const authorization = task?.authorization || {};
  const expectedOutput = task?.expected_output_definition || task?.expected_output || {};

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{task?.title || 'Task'}</DialogTitle>
      <DialogContent>
        {loading && !task ? <CircularProgress size={24} /> : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary">Workstream</Typography>
                <Typography variant="body2" sx={{ fontWeight: 750 }}>
                  {task?.workstream?.capability_name || workstream?.capability_name}
                </Typography>
              </Box>
              <Chip color={statusColor(task?.status)} label={formatLabel(task?.status)} />
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary">Assigned To</Typography>
              <Typography variant="body2" sx={{ fontWeight: 750 }}>{userLabel(task?.assignee)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {workstream?.provider_business_unit?.display_name || workstream?.provider_business_unit?.business_unit_code}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Description</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{task?.description}</Typography>
            </Box>
            {task?.due_at ? (
              <Box>
                <Typography variant="caption" color="text.secondary">Due</Typography>
                <Typography variant="body2">{formatDate(task.due_at)}</Typography>
              </Box>
            ) : null}
            {task?.waiting_reason ? <Alert severity="warning">{task.waiting_reason}</Alert> : null}
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>Expected Output</Typography>
              <Typography variant="body2" sx={{ fontWeight: 750 }}>{expectedOutput.name}</Typography>
              <Typography variant="caption" color="text.secondary">{formatLabel(expectedOutput.kind)} · Required</Typography>
              {expectedOutput.description ? <Typography variant="body2" sx={{ mt: 0.5 }}>{expectedOutput.description}</Typography> : null}
            </Box>
            <TaskOutputSummary task={task} onError={setError} />
            {authorization.can_provide_output ? (
              <TaskOutputProvider task={task} workstream={workstream} onProvided={changed} onError={setError} />
            ) : null}
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 850, mb: 1 }}>Actions</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {authorization.can_start ? <Button startIcon={<PlayArrowRoundedIcon />} variant="contained" onClick={() => postAction('start')} disabled={busy}>Start Task</Button> : null}
                {authorization.can_wait ? <Button startIcon={<PauseCircleRoundedIcon />} color="warning" variant="outlined" onClick={() => setActionMode('wait')} disabled={busy}>Mark Waiting</Button> : null}
                {authorization.can_resume ? <Button startIcon={<RestartAltRoundedIcon />} variant="contained" onClick={() => postAction('resume')} disabled={busy}>Resume Task</Button> : null}
                {authorization.can_complete ? <Button startIcon={<CheckCircleRoundedIcon />} color="success" variant="contained" onClick={() => postAction('complete')} disabled={busy}>Complete Task</Button> : null}
                {authorization.can_reassign ? <Button variant="outlined" onClick={() => setActionMode('reassign')} disabled={busy}>Reassign</Button> : null}
                {authorization.can_cancel ? <Button startIcon={<CancelRoundedIcon />} color="error" variant="outlined" onClick={() => setActionMode('cancel')} disabled={busy}>Cancel Task</Button> : null}
              </Stack>
            </Box>
            {actionMode === 'wait' || actionMode === 'cancel' ? (
              <Stack spacing={1}>
                <TextField
                  label={actionMode === 'wait' ? 'Waiting Reason' : 'Cancellation Reason'}
                  required={actionMode === 'wait'}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  multiline
                  minRows={2}
                />
                <Stack direction="row" spacing={1}>
                  <Button onClick={() => setActionMode('')}>Back</Button>
                  <Button
                    variant="contained"
                    color={actionMode === 'cancel' ? 'error' : 'warning'}
                    disabled={busy || (actionMode === 'wait' && !reason.trim())}
                    onClick={() => postAction(actionMode, {
                      waiting_reason: actionMode === 'wait' ? reason.trim() : undefined,
                      cancellation_reason: actionMode === 'cancel' ? reason.trim() || undefined : undefined,
                    })}
                  >
                    Confirm
                  </Button>
                </Stack>
              </Stack>
            ) : null}
            {actionMode === 'reassign' ? (
              <Stack spacing={1}>
                <Autocomplete
                  options={users.filter((user) => user.cognito_sub !== task?.assignee_user_id)}
                  value={newAssignee}
                  getOptionLabel={userLabel}
                  isOptionEqualToValue={(option, value) => option.cognito_sub === value.cognito_sub}
                  onChange={(_event, value) => setNewAssignee(value)}
                  renderInput={(params) => <TextField {...params} label="New Assignee" required />}
                />
                <Stack direction="row" spacing={1}>
                  <Button onClick={() => setActionMode('')}>Back</Button>
                  <Button variant="contained" onClick={reassign} disabled={busy || !newAssignee}>Reassign</Button>
                </Stack>
              </Stack>
            ) : null}
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 850, mb: 1 }}>Activity</Typography>
              <Stack spacing={0.75}>
                {(task?.activity || []).length ? task.activity.map((activity) => (
                  <Box key={activity.activity_id}>
                    <Typography variant="body2">{activity.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {activity.actor_email || activity.actor_user_id || 'AMX user'} · {formatDate(activity.timestamp)}
                    </Typography>
                  </Box>
                )) : <Typography variant="body2" color="text.secondary">No Task activity recorded yet.</Typography>}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function WorkstreamTaskCard({ task, onOpen }) {
  const expectedOutput = task.expected_output_definition || task.expected_output || {};
  return (
    <Card variant="outlined" sx={{ borderRadius: 1.5 }}>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>{task.title}</Typography>
            <Typography variant="body2" color="text.secondary">Assigned to {userLabel(task.assignee)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {expectedOutput.name} · {formatLabel(expectedOutput.kind)}
              {task.due_at ? ` · Due ${formatDate(task.due_at)}` : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" alignSelf={{ xs: 'stretch', sm: 'flex-start' }}>
            <Chip size="small" color={statusColor(task.status)} label={formatLabel(task.status)} />
            <Button size="small" variant="outlined" onClick={() => onOpen(task)}>Open</Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function WorkstreamTasks({ workstream, onRefresh, initialTaskId = '' }) {
  const tasks = workstream?.tasks;
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const handledInitialTask = useRef('');

  useEffect(() => {
    if (!initialTaskId || !Array.isArray(tasks)) return;
    const targetKey = `${workstream?.request_capability_id || ''}:${initialTaskId}`;
    if (handledInitialTask.current === targetKey) return;
    const target = tasks.find((task) => taskId(task) === initialTaskId);
    handledInitialTask.current = targetKey;
    if (target) setSelectedTask(target);
  }, [initialTaskId, tasks, workstream?.request_capability_id]);

  if (!Array.isArray(tasks)) return null;

  const activeCount = tasks.filter((task) => ['assigned', 'in_progress', 'waiting'].includes(task.status)).length;
  return (
    <Box>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>Provider Tasks</Typography>
            <Typography variant="caption" color="text.secondary">
              {tasks.length} total · {activeCount} active · Provider internal
            </Typography>
          </Box>
          {workstream?.authorization?.can_create_task ? (
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setCreateOpen(true)}>
              Create Task
            </Button>
          ) : null}
        </Stack>
        {tasks.length ? tasks.map((task) => (
          <WorkstreamTaskCard key={taskId(task)} task={task} onOpen={setSelectedTask} />
        )) : (
          <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 750 }}>No internal Tasks have been created for this workstream.</Typography>
            <Typography variant="body2" color="text.secondary">
              Tasks are optional. Use them when this work needs to be divided among members of your Business Unit.
            </Typography>
          </Box>
        )}
      </Stack>
      <CreateTaskDialog
        open={createOpen}
        workstream={workstream}
        onClose={() => setCreateOpen(false)}
        onCreated={onRefresh}
      />
      <WorkstreamTaskDetail
        open={Boolean(selectedTask)}
        initialTask={selectedTask}
        workstream={workstream}
        onClose={() => setSelectedTask(null)}
        onChanged={onRefresh}
      />
    </Box>
  );
}
